import { useState, useEffect } from "react";
import { Mail, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { EMAIL_NUDGES_ENABLED } from "@/config/featureFlags";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Nudge email preference row shown inside SettingsPanel when the user is
 * signed in. Reuses reminder_preferences.email on the backend so users can
 * route nudge emails to a different address than their Google sign-in
 * account, with a soft magic-link verification flow.
 *
 * All data-testids match the original SettingsPanel implementation so
 * existing test coverage keeps working.
 */
export const NudgeEmailSection = ({ isOpen, isAuthenticated, user }) => {
  // Feature flag: while the Resend sender domain isn't verified, we don't
  // let users configure an address they can't actually receive from.
  // Instead we show a warm "coming soon" card so the app still feels
  // complete. Everything below is preserved verbatim behind the flag.
  if (!EMAIL_NUDGES_ENABLED) {
    return (
      <div
        className="p-4 bg-[#FDFBF7] rounded-xl border border-stone-200 space-y-2"
        data-testid="nudge-email-coming-soon"
      >
        <div className="flex items-center gap-2 text-[#0A9699]">
          <Sparkles size={14} />
          <span className="text-xs font-semibold uppercase tracking-wider">Coming soon</span>
        </div>
        <p className="text-sm text-[#27272A] font-medium leading-snug">
          Email nudges are almost ready.
        </p>
        <p className="text-xs text-[#71717A] leading-relaxed">
          We're finishing the final piece so nudges land reliably in your inbox. When it ships, we'll route to your account email ({user?.email}) unless you set another one here.
        </p>
      </div>
    );
  }

  return <NudgeEmailSectionInner isOpen={isOpen} isAuthenticated={isAuthenticated} user={user} />;
};

// Original interactive implementation, preserved for when the flag flips
// back to true. Keeping it as a nested component means we don't have to
// gate every hook inside a conditional (React rules-of-hooks).
const NudgeEmailSectionInner = ({ isOpen, isAuthenticated, user }) => {
  const [nudgeEmail, setNudgeEmail] = useState('');
  const [nudgeEmailInitial, setNudgeEmailInitial] = useState('');
  const [nudgeEmailSaving, setNudgeEmailSaving] = useState(false);
  const [nudgeEmailSaved, setNudgeEmailSaved] = useState(false);
  const [nudgeEmailVerified, setNudgeEmailVerified] = useState(true);
  const [testNudgeSending, setTestNudgeSending] = useState(false);
  const [nudgeQuota, setNudgeQuota] = useState(null); // {active, limit, pro}

  // Load quota alongside the nudge email preference
  useEffect(() => {
    if (!isOpen || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/nudge/quota`, { credentials: 'include' });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        setNudgeQuota(data);
      } catch (err) {
        // Non-blocking — leave quota null
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isAuthenticated]);

  useEffect(() => {
    if (!isOpen || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/nudge/email`, { credentials: 'include' });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const val = data.override || data.email || '';
        setNudgeEmail(val);
        setNudgeEmailInitial(val);
        setNudgeEmailVerified(data.verified !== false);
      } catch (err) {
        console.error('Failed to load nudge email preference', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isAuthenticated]);

  const handleSaveNudgeEmail = async () => {
    if (nudgeEmail === nudgeEmailInitial) return;
    setNudgeEmailSaving(true);
    try {
      const isClearing = !nudgeEmail.trim();
      const res = await fetch(`${API_URL}/api/nudge/email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: isClearing ? null : nudgeEmail.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Couldn't save that email");
        return;
      }
      const data = await res.json();
      setNudgeEmailInitial(nudgeEmail);
      setNudgeEmailSaved(true);
      setNudgeEmailVerified(!data.override); // clearing = verified; new address = pending
      setTimeout(() => setNudgeEmailSaved(false), 2000);
      if (isClearing) {
        toast.success('Using your account email for nudges');
      } else if (data.verificationSent) {
        toast.success('Nudges routing here — check your inbox to confirm the address.');
      } else if (data.verificationAvailable === false) {
        toast.success('Nudge email updated');
      } else {
        toast.success('Nudge email updated');
      }
    } catch (err) {
      toast.error("Couldn't save that email");
    } finally {
      setNudgeEmailSaving(false);
    }
  };

  const handleSendTestNudge = async () => {
    if (testNudgeSending) return;
    setTestNudgeSending(true);
    try {
      const res = await fetch(`${API_URL}/api/nudge/email/test`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Couldn't send that test.");
        return;
      }
      if (data.success === false && data.error === 'email_not_configured') {
        toast(data.message || 'Email sending is not set up in this environment.');
        return;
      }
      toast.success(`Sample nudge sent to ${data.email}.`);
    } catch (err) {
      toast.error("Couldn't send that test.");
    } finally {
      setTestNudgeSending(false);
    }
  };

  return (
    <div className="p-3 bg-[#FDFBF7] rounded-xl border border-stone-200 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs font-medium text-[#52525B]" htmlFor="nudge-email-input">
          <Mail size={12} className="text-[#0A9699]" />
          Send my nudges to
        </label>
        {nudgeQuota && nudgeQuota.limit != null && (
          <span
            className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
              nudgeQuota.pro
                ? 'bg-[#E0F7FA] text-[#00838F]'
                : nudgeQuota.active >= (nudgeQuota.limit || 0)
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-stone-100 text-stone-500'
            }`}
            data-testid="nudge-quota-chip"
            title={nudgeQuota.pro ? 'Pro plan — unlimited nudges' : `Free plan: max ${nudgeQuota.limit} active nudges at a time`}
          >
            {nudgeQuota.pro
              ? `${nudgeQuota.active} active · Pro`
              : `${nudgeQuota.active} of ${nudgeQuota.limit} nudges in use`}
          </span>
        )}
      </div>

      {/* Explicit "currently routing here" line — makes the active
          address obvious independent of the input value, which
          might be mid-edit. */}
      <div
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-stone-100 text-[12px] text-[#52525B]"
        data-testid="nudge-email-current"
      >
        <Check size={11} className="text-[#0A9699] flex-shrink-0" />
        <span className="text-[#71717a]">Currently routing to:</span>
        <span className="font-medium text-[#27272A] truncate">{nudgeEmailInitial || user?.email}</span>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="nudge-email-input"
          type="email"
          value={nudgeEmail}
          onChange={(e) => setNudgeEmail(e.target.value)}
          placeholder={user?.email || 'your@email.com'}
          className="flex-1 bg-white rounded-lg px-3 py-2 text-sm border border-stone-200 outline-none focus:border-[#0A9699] transition-colors"
          data-testid="nudge-email-input"
          autoComplete="email"
        />
        <button
          onClick={handleSaveNudgeEmail}
          disabled={nudgeEmailSaving || nudgeEmail === nudgeEmailInitial}
          className={`px-3 py-2 rounded-lg text-xs font-medium transition-all min-w-[68px] flex items-center justify-center gap-1 ${
            nudgeEmailSaving || nudgeEmail === nudgeEmailInitial
              ? 'bg-stone-100 text-stone-400 cursor-not-allowed'
              : 'bg-[#0A9699] text-white hover:bg-[#087679]'
          }`}
          data-testid="nudge-email-save-btn"
        >
          {nudgeEmailSaved ? <Check size={14} /> : nudgeEmailSaving ? '...' : 'Save'}
        </button>
      </div>
      <p className="text-[11px] text-[#A1A1AA]">
        Leave blank to use your account email ({user?.email}).
      </p>
      {nudgeEmailInitial && !nudgeEmailVerified && nudgeEmail === nudgeEmailInitial && (
        <p className="text-[11px] text-amber-600 flex items-center gap-1 pt-1">
          <Mail size={11} />
          Confirmation pending — check {nudgeEmailInitial} for the link.
        </p>
      )}

      <button
        onClick={handleSendTestNudge}
        disabled={testNudgeSending}
        className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
          testNudgeSending
            ? 'bg-stone-100 text-stone-400 border-stone-200 cursor-wait'
            : 'bg-white text-[#0A9699] border-stone-200 hover:bg-[#F6FAFA] hover:border-[#0A9699]'
        }`}
        data-testid="nudge-email-test-btn"
      >
        {testNudgeSending ? (
          <>Sending sample nudge…</>
        ) : (
          <>
            <Mail size={12} /> Send me a test nudge
          </>
        )}
      </button>
    </div>
  );
};

export default NudgeEmailSection;
